import React, { useState, useEffect } from "react";
import axios from "axios";
import "bootstrap/dist/css/bootstrap.min.css";
import { API } from "../../API";

const AudioUploader = () => {
    const [audioFile, setAudioFile] = useState(null);
    const [thumbnailFile, setThumbnailFile] = useState(null);
    const [audioName, setAudioName] = useState("");
    const [loading, setLoading] = useState(false);
    const [audioList, setAudioList] = useState([]);
    const [errors, setErrors] = useState({});

    const handleFileChange = (e) => {
        setAudioFile(e.target.files[0]);
    };

    const handleThumbnailChange = (e) => {
        setThumbnailFile(e.target.files[0]);
    };

    const validateForm = () => {
        const newErrors = {};
        if (!audioName.trim()) {
            newErrors.audioName = "Audio name is required.";
        }
        if (!audioFile) {
            newErrors.audioFile = "Audio file is required.";
        } else if (audioFile.size > 20 * 1024 * 1024) {
            newErrors.audioFile = "Audio file size must not exceed 20MB.";
        }
        if (!thumbnailFile) {
            newErrors.thumbnailFile = "Thumbnail image is required.";
        } else if (thumbnailFile.size > 5 * 1024 * 1024) {
            newErrors.thumbnailFile = "Thumbnail size must not exceed 5MB.";
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const fetchAudioFiles = async () => {
        try {
            const response = await axios.get(`${API}/api/get-audios`);
            setAudioList(response.data.audios);
        } catch (error) {
            console.error("Error fetching audio files:", error);
            alert("Failed to fetch audio files.");
        }
    };

    const handleUpload = async () => {
        if (!validateForm()) return;

        const formData = new FormData();
        formData.append("audio", audioFile);
        formData.append("name", audioName);
        formData.append("thumbnail", thumbnailFile);

        setLoading(true);

        try {
            const response = await axios.post(
                `${API}/api/upload-audio`,
                formData,
                {
                    headers: {
                        "Content-Type": "multipart/form-data",
                    },
                }
            );
            const audioUrl = response.data?.audio?.audioUrl;
            if (audioUrl) {
                setLoading(false);

                alert("Uploaded successfully!");
                fetchAudioFiles();
                setAudioName("");
                setAudioFile(null);
                setThumbnailFile(null);
            } else {
                setLoading(false);

                alert("Upload succeeded but no URL returned.");
            }
        } catch (error) {
            console.error("Error uploading audio:", error);
            alert("Failed to upload audio.");
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (audioId) => {
        if (!window.confirm("Are you sure you want to delete this audio?")) return;

        try {
            await axios.delete(`${API}/api/delete-audio/${audioId}`);
            alert("Audio deleted successfully!");
            fetchAudioFiles();
        } catch (error) {
            console.error("Error deleting audio:", error);
            alert("Failed to delete audio.");
        }
    };

    useEffect(() => {
        fetchAudioFiles();
    }, []);

    return (
        <div className="container mt-5">
            <div className="card shadow-lg p-4 mb-4">
                <h2 className="text-center mb-4">Upload Audio</h2>
                <div className="mb-3">
                    <label htmlFor="audioName" className="form-label">
                        Audio Name <span className="text-danger">*</span>
                    </label>
                    <input
                        type="text"
                        className={`form-control ${errors.audioName ? "is-invalid" : ""}`}
                        id="audioName"
                        value={audioName}
                        onChange={(e) => setAudioName(e.target.value)}
                        placeholder="Enter audio name"
                        disabled={loading}
                    />
                    {errors.audioName && <div className="invalid-feedback">{errors.audioName}</div>}
                </div>
                <div className="mb-3">
                    <label htmlFor="audioFile" className="form-label">
                        Select an audio file <span className="text-danger">*</span>
                    </label>
                    <input
                        type="file"
                        accept="audio/*"
                        className={`form-control ${errors.audioFile ? "is-invalid" : ""}`}
                        id="audioFile"
                        onChange={handleFileChange}
                        disabled={loading}
                    />
                    {errors.audioFile && <div className="invalid-feedback">{errors.audioFile}</div>}
                </div>
                <div className="mb-3">
                    <label htmlFor="thumbnailFile" className="form-label">
                        Select a thumbnail image <span className="text-danger">*</span>
                    </label>
                    <input
                        type="file"
                        accept="image/*"
                        className={`form-control ${errors.thumbnailFile ? "is-invalid" : ""}`}
                        id="thumbnailFile"
                        onChange={handleThumbnailChange}
                        disabled={loading}
                    />
                    {errors.thumbnailFile && <div className="invalid-feedback">{errors.thumbnailFile}</div>}
                </div>
                <button
                    className="btn btn-primary w-100"
                    onClick={handleUpload}
                    disabled={loading}
                >
                    {loading ? (
                        <div className="spinner-border spinner-border-sm text-light" role="status"></div>
                    ) : (
                        "Upload"
                    )}
                </button>
            </div>

            <div className="card shadow-lg p-4">
                <h3 className="text-center mb-4">Uploaded Audios</h3>
                {audioList.length > 0 ? (
                    <ul
                        className="list-group"
                        style={{
                            maxHeight: "300px",
                            overflowY: "auto",
                        }}
                    >
                        {audioList.map((audio) => (
                            <li
                                key={audio._id}
                                className="list-group-item d-flex justify-content-between align-items-center"
                            >
                                <div>
                                    <img
                                        src={audio.thumbnail || "placeholder-thumbnail.png"}
                                        alt="Thumbnail"
                                        className="me-3"
                                        style={{ width: "50px", height: "50px", objectFit: "cover", borderRadius: "5px" }}
                                    />
                                    <span>{audio.name}</span>
                                </div>
                                <div className="d-flex align-items-center">
                                    <audio controls className="me-3">
                                        <source src={audio.audioUrl} type="audio/mpeg" />
                                        Your browser does not support the audio element.
                                    </audio>
                                    <button
                                        className="btn btn-danger btn-sm"
                                        onClick={() => handleDelete(audio._id)}
                                    >
                                        Delete
                                    </button>
                                </div>
                            </li>
                        ))}
                    </ul>
                ) : (
                    <p className="text-center">No audio files uploaded yet.</p>
                )}
            </div>
        </div>
    );
};

export default AudioUploader;
